"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import * as NavigationMenu from "@radix-ui/react-navigation-menu";
import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import { cn } from "@repo/ui/utils.ts";
import { Icon } from "@repo/ui/icon";
import { Button } from "@repo/ui/button";
import { Link, usePathname, useRouter } from "@/i18n/navigation";
import type { Locale } from "@/i18n/config";
import { useAudience } from "@/components/common/audience";
import { ActionLink } from "@/components/common/actions/actions.component";
import { getActionUrl } from "@/lib/actions/get-action-url";
import { PLANNER_ENABLED } from "@/lib/feature-flags";
import type { NavigationData, RichMenuItem } from "@/components/navigation/navigation.types";
import { useNavigationState } from "@/components/navigation/navigation-provider";
import { useHomeHref } from "@/components/navigation/use-home-href";
import { RichMenuDropdown } from "./rich-menu-dropdown.component";
interface PrimaryNavbarProps {
  logo: NavigationData["logo"];
  menuItems: NavigationData["primaryMenuItems"];
  businessMenuItems: NavigationData["businessPrimaryMenuItems"];
}
const emptySubscribe = () => () => {};

/** Matches the viewport open transition duration below (duration-300). */
const OPEN_TRANSITION_MS = 300;

/** Stateful mega menu — keyed on pathname in PrimaryNavbar so it resets on navigation */
function MegaMenu(t0) {
  const $ = _c(29);
  const {
    menuItems
  } = t0;
  const [value, setValue] = useState("");
  const [maxHeight, setMaxHeight] = useState(null);
  const measurerRef = useRef(null);
  const isOpeningRef = useRef(false);
  const openingTimerRef = useRef(undefined);
  const locale = useLocale() as Locale;
  const router = useRouter();
  const mounted = useSyncExternalStore(emptySubscribe, _temp, _temp2);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = next => {
      setValue(next);
      clearTimeout(openingTimerRef.current);
      isOpeningRef.current = Boolean(next);
      if (next) {
        openingTimerRef.current = setTimeout(() => {
          isOpeningRef.current = false;
        }, OPEN_TRANSITION_MS);
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleValueChange = t1;
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => () => clearTimeout(openingTimerRef.current);
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const node = measurerRef.current;
      if (!node) {
        return;
      }
      const recompute = () => {
        let tallest = 0;
        for (const child of Array.from(node.children)) {
          tallest = Math.max(tallest, (child as HTMLElement).offsetHeight);
        }
        if (tallest > 0) {
          setMaxHeight(tallest);
        }
      };
      recompute();
      const observer = new ResizeObserver(recompute);
      for (const child_0 of Array.from(node.children)) {
        observer.observe(child_0);
      }
      return () => observer.disconnect();
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== menuItems) {
    t5 = [menuItems];
    $[4] = menuItems;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] !== menuItems) {
    t6 = menuItems?.filter(_temp3).map(_temp4);
    $[6] = menuItems;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t6) {
    t7 = <div ref={measurerRef} aria-hidden="true" className="invisible pointer-events-none absolute left-0 right-0 top-full -z-10">{t6}</div>;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== locale || $[11] !== maxHeight || $[12] !== menuItems || $[13] !== router || $[14] !== value) {
    t8 = menuItems && menuItems.map(item_1 => {
      if (item_1.type === "action") {
        return <NavigationMenu.Item key={item_1.id} value={item_1.id}><ActionLink actionTo={item_1.to ?? undefined} className="group flex cursor-pointer whitespace-nowrap px-[18px] py-1 typography-component-sm-medium text-text-primary transition-colors hover:text-text-interactive"><span className="border-b border-transparent pb-0.5 transition-colors group-hover:border-border-interactive">{item_1.heading}</span></ActionLink></NavigationMenu.Item>;
      }
      const link = item_1.to ? getActionUrl(item_1.to, locale) : null;
      return <NavigationMenu.Item key={item_1.id} value={item_1.id}><NavigationMenu.Trigger onClick={event => {
          if (link) {
            event.preventDefault();
            if (link.isExternal) {
              window.open(link.href, link.target ?? "_self");
            } else {
              router.push(link.href);
            }
            return;
          }
          if (value === item_1.id && isOpeningRef.current) {
            event.preventDefault();
          }
        }} className="group relative cursor-pointer whitespace-nowrap px-[18px] py-1 typography-component-sm-medium text-text-primary transition-colors hover:text-text-interactive after:absolute after:inset-x-0 after:top-full after:hidden after:h-10 after:content-[''] data-[state=open]:after:block"><span className="border-b border-transparent pb-0.5 transition-colors group-hover:border-border-interactive group-data-[state=open]:border-border-interactive">{item_1.heading}</span></NavigationMenu.Trigger><NavigationMenu.Content className="absolute left-0 top-0 w-full" onClick={event_0 => {
          if ((event_0.target as HTMLElement).closest("a")) {
            setValue("");
          }
        }}><RichMenuDropdown menu={item_1} minHeight={maxHeight ?? undefined} /></NavigationMenu.Content></NavigationMenu.Item>;
    });
    $[10] = locale;
    $[11] = maxHeight;
    $[12] = menuItems;
    $[13] = router;
    $[14] = value;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t8) {
    t9 = <NavigationMenu.List className="flex items-center">{t8}</NavigationMenu.List>;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="absolute left-0 right-0 top-full w-full"><NavigationMenu.Viewport forceMount={true} className={cn("relative w-full overflow-hidden border-t border-border-subtle bg-surface-base-primary", "origin-top transition-[height,opacity,transform] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]", "h-[var(--radix-navigation-menu-viewport-height)]", "data-[state=open]:opacity-100 data-[state=open]:translate-y-0", "data-[state=closed]:pointer-events-none data-[state=closed]:opacity-0 data-[state=closed]:-translate-y-1 data-[state=closed]:h-0 data-[state=closed]:border-t-0")} /></div>;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t9 || $[20] !== value) {
    t11 = <NavigationMenu.Root className="static flex-1" value={value} onValueChange={handleValueChange} aria-label="Main navigation">{t9}{t10}</NavigationMenu.Root>;
    $[19] = t9;
    $[20] = value;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== mounted || $[23] !== value) {
    t12 = value && mounted && createPortal(<div className="fixed inset-0 top-[var(--navbar-height,104px)] z-40 bg-black/30 transition-opacity duration-300" aria-hidden="true" onClick={() => setValue("")} />, document.body);
    $[22] = mounted;
    $[23] = value;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t11 || $[26] !== t12 || $[27] !== t7) {
    t13 = <>{t7}{t11}{t12}</>;
    $[25] = t11;
    $[26] = t12;
    $[27] = t7;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  return t13;
}
function _temp4(item_0) {
  return <RichMenuDropdown key={item_0.id} menu={item_0} />;
}
function _temp3(item) {
  return item.type === "richMenu";
}
function _temp2() {
  return false;
}
function _temp() {
  return true;
}
export function PrimaryNavbar(t0) {
  const $ = _c(25);
  const {
    logo,
    menuItems,
    businessMenuItems
  } = t0;
  const t = useTranslations("navigation");
  const pathname = usePathname();
  const {
    openSearch
  } = useNavigationState();
  const homeHref = useHomeHref();
  const {
    activeTab
  } = useAudience();
  const isBusinesses = activeTab === "businesses";
  const logoSrc = isBusinesses ? logo?.dark ?? logo?.light ?? "/images/va-waterfront-logo-light.svg" : logo?.light ?? "/images/va-waterfront-logo.svg";
  const logoAlt = logo?.alt ?? "V&A Waterfront Cape Town";
  const activeMenuItems = isBusinesses ? businessMenuItems : menuItems;
  let t1;
  if ($[0] !== logoAlt || $[1] !== logoSrc) {
    t1 = <Image src={logoSrc} alt={logoAlt} width={179} height={48} className="h-12 w-[179px]" />;
    $[0] = logoAlt;
    $[1] = logoSrc;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== homeHref || $[4] !== t1) {
    t2 = <Link href={homeHref} className="flex-shrink-0">{t1}</Link>;
    $[3] = homeHref;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = `${pathname}-${activeTab}`;
  let t4;
  if ($[6] !== activeMenuItems || $[7] !== t3) {
    t4 = <MegaMenu key={t3} menuItems={activeMenuItems} />;
    $[6] = activeMenuItems;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== isBusinesses || $[10] !== t) {
    t5 = PLANNER_ENABLED && !isBusinesses && <Button variant="blue-filled" size="sm" asChild={true}><Link href="/plan-your-visit" className="whitespace-nowrap">{t("planYourTrip")}</Link></Button>;
    $[9] = isBusinesses;
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t) {
    t6 = t("openSearch");
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Icon name="search" size="lg" />;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== openSearch || $[16] !== t6) {
    t8 = <button type="button" onClick={openSearch} className="flex items-center justify-center rounded-full border border-border-subtle p-2 text-text-primary transition-colors hover:border-text-primary" aria-label={t6}>{t7}</button>;
    $[15] = openSearch;
    $[16] = t6;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t4 || $[19] !== t5 || $[20] !== t8) {
    t9 = <div className="flex flex-1 items-center gap-4">{t4}{t5}{t8}</div>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t2 || $[23] !== t9) {
    t10 = <div className="relative border-b border-border-subtle py-6"><div className="flex items-center gap-5 px-12">{t2}{t9}</div></div>;
    $[22] = t2;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
}